/**
 * JQ4-17 · US-CONFIG-003 — Hotel exit/re-entry mutation hooks.
 *
 * useRegisterExit  — POST /valet/tickets/:id/exits
 * useRegisterReentry — PATCH /valet/tickets/:id/exits/latest/re-entry
 *
 * Pattern mirrors useDeliverTicket (JQ4-26): call api-client, invalidate
 * relevant query cache on success.
 */

import { registerTicketExit, registerTicketReentry } from '@jq4race/api-client';
import { useMutation, useQueryClient } from '@tanstack/react-query';

// ── Cache key for hotel exits list ───────────────────────────────────────────

export const ticketExitsKey = (ticketId: string) => ['ticket-exits', ticketId] as const;

// ── useRegisterExit ───────────────────────────────────────────────────────────

/**
 * Register a vehicle exit for a Hotel-niche parked ticket.
 *
 * On success: invalidates the exits list so the vehicle-OUT indicator updates.
 * On max_exits_exceeded (422): propagates the error to the caller for display.
 */
export function useRegisterExit(ticketId: string) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: () => registerTicketExit(ticketId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ticketExitsKey(ticketId) });
    },
  });
}

// ── useRegisterReentry ────────────────────────────────────────────────────────

/**
 * Close the most-recent open exit (vehicle re-entered the premises).
 *
 * On success: invalidates exits list so the vehicle-OUT indicator clears.
 */
export function useRegisterReentry(ticketId: string) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: () => registerTicketReentry(ticketId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ticketExitsKey(ticketId) });
    },
  });
}
